/**
 * Règle d'or des permissions (sécurité UI) :
 * - SI user.is_owner === true → Accès total (return true pour toute clé).
 * - SINON SI team_role === 'admin' (Second Admin) → Accès total.
 * - SINON (membre avec team_role 'member' ou non défini) → return user.permissions[permissionKey] (strict).
 */

import type { User } from "../types";
import type { TeamPermissions } from "../types";

export type PermissionKey = keyof TeamPermissions;

const ALL_PERMISSION_KEYS: PermissionKey[] = [
  "canViewJobs",
  "canCreateJobs",
  "canEditJobs",
  "canDeleteJobs",
  "canManageStock",
  "canManagePartners",
  "canManageInstallers",
  "canViewMainHistory",
  "canManageTeam",
];

/**
 * Retourne true UNIQUEMENT si :
 * - user.is_owner === true (owner = accès total), ou
 * - user.team_role === 'admin' (Second Admin = accès total),
 * - sinon (membre) : true seulement si user.permissions[key] === true (strict, pas de fallback).
 */
export function hasPermission(
  user: User | null | undefined,
  key: PermissionKey
): boolean {
  if (!user) return false;
  if (user.is_owner === true) return true;
  if (user.team_role === "admin") return true;
  return user.permissions?.[key] === true;
}

/**
 * Helper/Hook : retourne une fonction hasPermission liée à l'utilisateur.
 * Usage : const { hasPermission } = usePermission(currentUser); puis hasPermission('canManageStock')
 */
export function usePermission(user: User | null | undefined) {
  return {
    hasPermission: (key: PermissionKey) => hasPermission(user, key),
  };
}

/**
 * Retourne un objet de permissions effectives (pour éviter d'appeler hasPermission partout).
 * Owner / Second Admin : toutes à true. Membre : uniquement celles à true dans user.permissions.
 */
export function getEffectivePermissions(
  user: User | null | undefined
): Record<PermissionKey, boolean> {
  const result = {} as Record<PermissionKey, boolean>;
  for (const key of ALL_PERMISSION_KEYS) {
    result[key] = hasPermission(user, key);
  }
  return result;
}

/**
 * Vérifie si l'utilisateur a au moins un des droits "dossiers" (voir/créer/modifier/supprimer).
 */
export function canSeeJobsTab(user: User | null | undefined): boolean {
  return (
    hasPermission(user, "canViewJobs") ||
    hasPermission(user, "canCreateJobs") ||
    hasPermission(user, "canEditJobs") ||
    hasPermission(user, "canDeleteJobs")
  );
}

/**
 * Onglet Stock : visible seulement si canManageStock.
 */
export function canSeeStockTab(user: User | null | undefined): boolean {
  return hasPermission(user, "canManageStock");
}

/**
 * Onglet Gestion Accès (Partenaires/Installateurs) : visible si au moins un des deux droits.
 */
export function canSeeAccessTab(user: User | null | undefined): boolean {
  return (
    hasPermission(user, "canManagePartners") ||
    hasPermission(user, "canManageInstallers")
  );
}

/**
 * Onglet Mon Équipe : visible seulement si hasPermission('canManageTeam').
 */
export function canSeeTeamTab(user: User | null | undefined): boolean {
  return hasPermission(user, "canManageTeam");
}
